import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from '../../lib/api';
import type { TrackSummary } from './TrackList';

/**
 * Somebody's uploads, in pages, for every list that shows them.
 *
 * ## Why this is a hook and not five queries
 *
 * Five places asked for `['tracks', username]` - the profile list, the
 * album's picker, the swap dialog, the room's crate and the submit
 * dialog - and when one of them became a paged query the others still
 * expected `{ items }` from the same cache entry. Whichever loaded first
 * decided the shape: the picker showed "you have no finished tracks yet"
 * on a catalogue of sixty, or the profile threw reading `pages` of
 * something that had none. One shape, in one place, is the only way that
 * stays true.
 *
 * ## `singles`
 *
 * What the profile's own list asks for: the tracks that are not on one of
 * this artist's releases, because the albums are listed right above it. It
 * is a different question about the same catalogue, so it is a different
 * cache entry - `['tracks', name, 'singles']` beside `['tracks', name,
 * 'all']` - and both come out of here in one shape, which is the whole
 * point of this hook.
 *
 * ## `all`
 *
 * A profile shows a page and a button. A picker is a list somebody is
 * choosing from, and "the rest of your tracks are behind a button you
 * cannot see" is how the missing ones get missed - so the pickers walk
 * every page. Nobody has a catalogue where that is expensive, and the
 * request that proves there is no more page is the same one that used to
 * be the only request.
 */
export function useArtistTracks(
  username: string,
  {
    enabled = true,
    all = false,
    singles = false,
  }: { enabled?: boolean; all?: boolean; singles?: boolean } = {},
) {
  const query = useInfiniteQuery({
    queryKey: ['tracks', username, singles ? 'singles' : 'all'],
    queryFn: ({ pageParam }) => {
      const asked = [singles ? 'singles=1' : '', pageParam === null ? '' : `cursor=${pageParam}`]
        .filter((part) => part !== '')
        .join('&');

      return api.get<{ items: TrackSummary[]; next_cursor: string | null }>(
        `/users/${username}/tracks${asked === '' ? '' : `?${asked}`}`,
      );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: enabled && username !== '',
    /*
     * Poll while anything is still being worked on.
     *
     * Only while - a profile of finished tracks does not need a request
     * every three seconds. This is what makes "upload, then land back
     * here" work without a reload, and it stops on its own.
     */
    refetchInterval: (q) =>
      q.state.data?.pages.some((page) =>
        page.items.some((track) => track.status === 'processing' || track.status === 'pending'),
      )
        ? 3000
        : false,
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => {
    if (!all || !hasNextPage || isFetchingNextPage) return;

    void fetchNextPage();
  }, [all, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    ...query,
    /** Every row loaded so far, flattened, in the order the server gave them. */
    items: query.data?.pages.flatMap((page) => page.items) ?? [],
  };
}
